import React, { useState, useRef, useEffect, useCallback } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { formatLocation } from "../../../../utils/formatLocation";

export const CollegeDropdown = ({
  selectedCollege,
  onSelectCollege,
  colleges,
  loading = false,
  onSearch,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  onDropdownOpen
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setInputValue("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleFocus = () => {
    setIsOpen(true);
    if (onDropdownOpen) onDropdownOpen();
    if (onSearch) onSearch("");
  };

  const handleInputChange = (value) => {
    setInputValue(value);
    if (onSearch) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onSearch(value);
      }, 400);
    }
  };

  const handleSelect = (college) => {
    onSelectCollege(college);
    setIsOpen(false);
    setInputValue("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setInputValue("");
    if (onSearch) onSearch("");
    inputRef.current?.focus();
    setIsOpen(true);
  };

  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || loadingMore || !hasMore || !onLoadMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 80) onLoadMore();
  }, [loading, loadingMore, hasMore, onLoadMore]);

  const displayedColleges = onSearch
    ? colleges
    : colleges.filter((c) =>
        (c.name || "").toLowerCase().includes(inputValue.toLowerCase())
      );

  return (
    <div className="relative w-64" ref={containerRef}>
      {/* Search Input */}
      <div className="relative flex items-center">
        <FiSearch
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-jp-muted pointer-events-none"
          size={13}
        />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onFocus={handleFocus}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder="Search team..."
          className="w-full pl-8 pr-7 py-1.5 bg-jp-section border border-jp-line rounded-lg text-xs text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-jp-muted hover:text-jp-ink transition-colors"
          >
            <FiX size={12} />
          </button>
        )}
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-jp-section border border-jp-line rounded-lg shadow-xl z-50 overflow-hidden">
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-56 overflow-y-auto"
          >
            {loading && displayedColleges.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : displayedColleges.length > 0 ? (
              <>
                {displayedColleges.map((college) => {
                  const id = college._id || college.id;
                  const isSelected =
                    selectedCollege?._id === id || selectedCollege?.id === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleSelect(college)}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                        isSelected
                          ? "bg-jp-accent/15 text-jp-accent-ink font-semibold"
                          : "text-jp-ink hover:bg-jp-line"
                      }`}
                    >
                      {college.name}
                      {college.location && (
                        <span className="block text-[10px] text-jp-muted mt-0.5">
                          {formatLocation(college.location)}
                        </span>
                      )}
                    </button>
                  );
                })}
                {loadingMore && (
                  <div className="px-3 py-2 text-center">
                    <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                )}
                {hasMore && !loadingMore && (
                  <div className="px-3 py-1 text-center text-jp-muted text-[10px]">
                    Scroll for more...
                  </div>
                )}
              </>
            ) : (
              <div className="px-3 py-6 text-center text-jp-muted text-xs">
                No teams found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
